
import React, { Suspense, LazyExoticComponent, FC, useEffect } from "react";
import { useRoutes, Navigate } from "react-router-dom";

// Layouts
import DefaultLayout from "../layouts/Default";
import VerticalLayout from "../layouts/Vertical";
import HorizontalLayout from "../layouts/Horizontal";

// Error Boundaries
import { ErrorBoundary } from "../components/ErrorBoundary/ErrorBoundary";
import ErrorFallback from "../components/ErrorBoundary/ErrorFallback";

// Redux & Tokens
import { useRedux } from "../hooks";
import { tokens } from "../tokens/index";
import { LayoutTypes } from "../constants";
import { sessionCheckAction } from "src/redux/actions";


// Components
import LoadingSpinner from "../components/FeedBack/LoadingSpinner";

// Lazy Pages
// const Home = React.lazy(() => import("../pages/Home/Home"));
const Login = React.lazy(() => import("../pages/Login/LoginPage"));
const Dashboard = React.lazy(() => import("../pages/Dashboard/DashBoard"));
const Selection = React.lazy(() => import("../pages/SelectionPage/SelectionPage"));
const BookingPage = React.lazy(() => import("../pages/BookingPage/BookingPage"));
const FoodPage = React.lazy(() => import("../pages/FoodPage/FoodPage"));
const BalancePage = React.lazy(() => import("../pages/BalancePage/BalancePage"));
const QueryPage = React.lazy(() => import("../pages/QueryPage/QueryPage"));
const BolBhai = React.lazy(() => import("../pages/BolBhai/HotelVoiceEntryScreen"));
const Camera = React.lazy(() => import("../pages/CameraPage/CameraPage"));
const BalanceRoom = React.lazy(() => import("../pages/balanceRoom/BalanceRoom"));
const Tailwind = React.lazy(() => import("../pages/Tailwind/Tailwindpage"));
const Home = React.lazy(() => import("../pages/HomePage/VoiceAssistantModal/VoiceAssistantModal"));

const PasskeyAuth = React.lazy(() => import("../pages/PasskeyAuth/index"));

const HomeMobile = React.lazy(() => import("../pages/HomePage/VoiceAssistantModal/VoiceAssistantModalMobile"));

const RestaurantMenu = React.lazy(() => import("../pages/HomePage/VoiceAssistantModal/RestaurantMenu"));

const StockItems = React.lazy(() => import("../pages/HomePage/VoiceAssistantModal/StockItems"));

const HomeWa = React.lazy(() => import("../pages/HomePage/VoiceAssistantModal/VoiceAssistantModalWa"));
const HomeScreen = React.lazy(() => import("../pages/HomePage/AvailableRoom"));
const HomeScreenOld = React.lazy(() => import("../pages/HomePage/old"));
const HomeBolBhai = React.lazy(() => import("../pages/HomeLanding/LandingPage"));
const BolBhaiLanding = React.lazy(() => import("../pages/BolBhaiLanding/BolBhaiLanding"));
const LandingBolBhai = React.lazy(() => import("../pages/BolBhaiLanding/Landing"));
const Branding = React.lazy(() => import ("../pages/Brand/brand"));
const HomeDemo = React.lazy(() => import ("../pages/HomePage/VoiceAssistantModal/VoiceAssistantModalMobileDemo"));

const WhatsappEmu = React.lazy(() => import ("../pages/whatsapp/whatsappEmu"));
const BolbhaiLoginExpired = React.lazy(() => import ("../pages/BolBhaiLanding/BolbhaiLoginExpired"));

// Menu 
const Hotel = React.lazy(() => import ("../pages/MenuPages/HotelPage/HotelPage"));
const Profile = React.lazy(() => import ("../pages/MenuPages/ProfilePage/ProfilePage"));
const Team = React.lazy(() => import ("../pages/MenuPages/TeamPage/TeamPage"));
const Pricing = React.lazy(() => import ("../pages/MenuPages/PricingPage/PricingPage"));



// ✅ Loading fallback
const LoadingFallback = () => (
  <div
    style={{
      display: tokens.layout.display.flex,
      justifyContent: tokens.layout.justify.center,
      alignItems: tokens.layout.align.center,
      height: tokens.viewport.fullHeight,
      background: tokens.color.background.secondary,
      animation: tokens.animations.fadeIn,
    }}
  >
    <LoadingSpinner size="sm" variant="default" />
  </div>
);

type LoadComponentProps = {
  component: LazyExoticComponent<FC<any>>;
};

const LoadComponent: FC<LoadComponentProps> = ({ component: Component }) => (
  <Suspense fallback={<LoadingFallback />}>
    <ErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => (
        <ErrorFallback error={error} resetErrorBoundary={resetErrorBoundary} />
      )}
    >
      <Component />
    </ErrorBoundary>
  </Suspense>
);

const AllRoutes = () => {
  const { dispatch , useAppSelector } = useRedux();
  const layout = useAppSelector((state) => state.Layout);


  // this dispatch added to laod the session cookies 
      useEffect(() => {
        dispatch(sessionCheckAction());
    }, []);

  // ✅ Determine Layout type
  const getLayout = () => {
    if (!layout || !layout.layoutType) return VerticalLayout;
    switch (layout.layoutType) {
      case LayoutTypes.LAYOUT_HORIZONTAL:
        return HorizontalLayout;
      default:
        return VerticalLayout;
    }
  };

  const Layout = getLayout();

  return useRoutes([
    // ✅ Public Routes (no app layout)
    {
      path: "/",
      element: <DefaultLayout />,
      children: [
        { path: "login", element: <LoadComponent component={Login} /> },
        { path: "select", element: <LoadComponent component={Selection} /> },
        // { path: "Bol_bhai", element: <LoadComponent component={BolBhai} /> },
        // { path: "home", element: <LoadComponent component={Home} /> },
      ],
    },

    // ✅ Protected App Routes (wrapped with Layout)
    {
      path: "/",
      // element: <Layout />,
      children: [
        // { index: true, element: <Navigate to="booking" /> }, 
        { index: true, element: <Navigate to="Landing" /> },

        { path: "Home", element: <LoadComponent component={Home} /> },
        { path: "HomeWa", element: <LoadComponent component={HomeWa} /> },
        { path: "HomeMobile", element: <LoadComponent component={HomeMobile} /> },
        { path: "RestaurantMenu", element: <LoadComponent component={RestaurantMenu} /> },
        { path: "StockItems", element: <LoadComponent component={StockItems} /> },
        // Menu pages
        { path: "profile", element: <LoadComponent component={Profile} /> },
        { path: "hotel", element: <LoadComponent component={Hotel} /> },
        { path: "team", element: <LoadComponent component={Team} /> },
        { path: "pricing", element: <LoadComponent component={Pricing} /> },

        { path: "BolBhaiLanding", element: <LoadComponent component={BolBhaiLanding} /> },
        { path: "LandingBolBhai", element: <LoadComponent component={LandingBolBhai} /> },
        { path: "HomeScreen", element: <LoadComponent component={HomeScreen} /> },
        { path: "HomeScreenOld", element: <LoadComponent component={HomeScreenOld} /> },
        { path: "booking", element: <LoadComponent component={BookingPage} /> },
        { path: "food", element: <LoadComponent component={FoodPage} /> },
        { path: "balanceRoom", element: <LoadComponent component={BalanceRoom} /> },
        { path: "balanceBill", element: <LoadComponent component={BalancePage} /> },
        { path: "query", element: <LoadComponent component={QueryPage} /> },
        { path: "camera", element: <LoadComponent component={Camera} /> },
        { path: "BolBhai", element: <LoadComponent component={BolBhai} /> },
        { path: "tailwind", element: <LoadComponent component={Tailwind} /> },
        { path: "Dashboard", element:<LoadComponent component={Dashboard} /> },
        { path: "Landing", element:<LoadComponent component={HomeBolBhai} /> },
        { path: "Branding", element:<LoadComponent component={Branding} /> },
        { path: "BolbhaiLoginExpired", element:<LoadComponent component={BolbhaiLoginExpired} /> },

        { path: "HomeDemo", element:<LoadComponent component={HomeDemo} /> },
        { path: "PasskeyAuth", element:<LoadComponent component={PasskeyAuth} /> },

        { path: "WhatsappEmu", element:<LoadComponent component={WhatsappEmu} /> },
      ],
    },

    // ✅ Fallback - unknown routes
    { path: "*", element: <Navigate to="Landing" /> },
  ]);
};

export { AllRoutes };
